import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { createEntityAdapter, Dictionary, EntityState } from '@reduxjs/toolkit';

import createSafeContext from '#/utils/createSafeContext';
import {
  FillNotification,
  TradeBustNotification,
  TransactionNotification,
} from '#/api/ws/ws';
import useLiveConnection, {
  LiveConnectionBaseState,
} from '#/utils/useLiveConnection';
import { usePerpetualMarketsState } from '#/features/perpetuals/market-info/markets-context';
import { Fill, FillsResp, getFills } from '#/api/fills';
import {
  getTransactions,
  Transaction,
  TransactionsResp,
} from '#/api/transactions';
import { getTradeBusts, TradeBust, TradeBustsResp } from '#/api/trade-busts';
import { Market } from '#/api/markets';
import { useWalletView } from '#/features/wallet/wallet-context';

import { fillView, FillView } from './fill-view';

const adapter = createEntityAdapter<Fill>({
  sortComparer: (a, b) =>
    b.id.localeCompare(a.id, undefined, { numeric: true }),
});
const selectors = adapter.getSelectors();

const transactionsAdapter = createEntityAdapter<Transaction>();
const transactionSelectors = transactionsAdapter.getSelectors();

const tradeBustsAdapter = createEntityAdapter<TradeBust>({
  selectId: (tradeBust) => tradeBust.busted_fill_id,
});
const tradeBustSelectors = tradeBustsAdapter.getSelectors();

const INITIAL_STATE: FillsState = {
  status: 'connected',
  error: '',
  fills: adapter.getInitialState(),
  transactions: transactionsAdapter.getInitialState(),
  tradeBusts: tradeBustsAdapter.getInitialState(),
};

export interface FillsState extends LiveConnectionBaseState {
  readonly fills: EntityState<Fill>;
  readonly transactions: EntityState<Transaction>;
  readonly tradeBusts: EntityState<TradeBust>;
}
interface FillsView extends Omit<FillsState, 'fills'> {
  readonly fills: readonly FillView[];
}

export const [useFillsView, FillsViewCtx] =
  createSafeContext<FillsView>('FillsView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function FillsProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);
  const { markets } = usePerpetualMarketsState();

  const view = useMemo(
    () => createView(state, markets.entities),
    [state, markets.entities],
  );

  useFillsFetch(setState);
  useTransactionsFetch(setState);
  useTradeBustsFetch(setState);

  return <FillsViewCtx value={view}>{props.children}</FillsViewCtx>;
}

export function createView(
  state: FillsState,
  markets: Dictionary<Market>,
): FillsView {
  const fills = selectors
    .selectAll(state.fills)
    .map((fill) =>
      fillView(
        fill,
        markets,
        transactionSelectors.selectById(state.transactions, fill.id),
        tradeBustSelectors.selectById(state.tradeBusts, fill.id),
      ),
    );

  return {
    ...state,
    fills,
  };
}

function useFillsFetch(setState: Dispatch<SetStateAction<FillsState>>): void {
  const walletView = useWalletView();

  const isReady = walletView.isSignedIn;

  const channel = isReady ? `fills.ALL` : '';

  const fetchAll = useCallback(
    async (signal: AbortSignal) =>
      getFills({
        signal,
      }),
    [],
  );

  const setAll = useCallback(
    (data: FillsResp) => {
      setState((state) => ({
        ...state,
        fills: adapter.setAll(state.fills, data.results),
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      fills: adapter.removeAll(state.fills),
    }));
  }, [setState]);

  const applyWSNotification = useCallback(
    (notif: FillNotification) => {
      setState((state) => ({
        ...state,
        fills: adapter.setOne(state.fills, notif.data),
      }));
    },
    [setState],
  );

  useLiveConnection<FillsResp, FillsState, FillNotification>({
    setState,
    channel,
    isReady,
    fetchAll,
    setAll,
    resetAll,
    applyWSNotification,
  });
}

function useTransactionsFetch(
  setState: Dispatch<SetStateAction<FillsState>>,
): void {
  const walletView = useWalletView();

  const isReady = walletView.isSignedIn;

  const channel = isReady ? 'transaction' : '';

  const fetchAll = useCallback(
    async (signal: AbortSignal) => getTransactions({ signal }),
    [],
  );

  const setAll = useCallback(
    (data: TransactionsResp) => {
      setState((state) => ({
        ...state,
        transactions: transactionsAdapter.setAll(
          state.transactions,
          data.results,
        ),
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      transactions: transactionsAdapter.removeAll(state.transactions),
    }));
  }, [setState]);

  const applyWSNotification = useCallback(
    (notif: TransactionNotification) => {
      setState((state) => ({
        ...state,
        transactions: transactionsAdapter.setOne(
          state.transactions,
          notif.data,
        ),
      }));
    },
    [setState],
  );

  useLiveConnection<TransactionsResp, FillsState, TransactionNotification>({
    setState,
    channel,
    isReady,
    fetchAll,
    setAll,
    resetAll,
    applyWSNotification,
  });
}

function useTradeBustsFetch(
  setState: Dispatch<SetStateAction<FillsState>>,
): void {
  const walletView = useWalletView();

  const isReady = walletView.isSignedIn;

  const channel = isReady ? 'tradebusts' : '';

  const fetchAll = useCallback(
    async (signal: AbortSignal) => getTradeBusts({ signal }),
    [],
  );

  const setAll = useCallback(
    (data: TradeBustsResp) => {
      setState((state) => ({
        ...state,
        tradeBusts: tradeBustsAdapter.setAll(state.tradeBusts, data.results),
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      tradeBusts: tradeBustsAdapter.removeAll(state.tradeBusts),
    }));
  }, [setState]);

  const applyWSNotification = useCallback(
    (notif: TradeBustNotification) => {
      setState((state) => ({
        ...state,
        tradeBusts: tradeBustsAdapter.setOne(state.tradeBusts, notif.data),
      }));
    },
    [setState],
  );

  useLiveConnection<TradeBustsResp, FillsState, TradeBustNotification>({
    setState,
    channel,
    isReady,
    fetchAll,
    setAll,
    resetAll,
    applyWSNotification,
  });
}
